import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Sparkles, Send } from "lucide-react";

export default function Aurora() {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Oi, eu sou a Aurora ✨\n\nSou uma assistente de apoio da Optia. Estou aqui pra escutar você, sem julgamentos. Como está seu dia hoje?" },
  ]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => { scrollRef.current?.scrollTo({ top: 99999, behavior: "smooth" }); }, [messages, busy]);

  const send = async () => {
    if (!input.trim() || busy) return;
    const msg = input.trim();
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/aurora/chat", { message: msg, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const suggestions = [
    "Estou me sentindo sobrecarregada(o) hoje",
    "Como lido com sobrecarga sensorial?",
    "Preciso de uma técnica de respiração",
    "Como falar sobre TDAH com meu chefe?",
  ];

  return (
    <div className="container-optia py-10 max-w-3xl animate-fade-in">
      <div className="flex items-center gap-3">
        <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-teal-400 to-navy-900 grid place-items-center text-white"><Sparkles className="w-5 h-5" /></div>
        <div>
          <h1 className="font-display text-2xl font-semibold text-navy-900">Aurora</h1>
          <p className="text-sm text-slate-600">Apoio emocional em português. Não substitui atendimento profissional.</p>
        </div>
      </div>

      <Card className="card-soft mt-6">
        <CardContent className="p-0">
          <div ref={scrollRef} className="h-[520px] overflow-y-auto p-6 space-y-4" data-testid="aurora-messages">
            {messages.map((m, i) => (
              <div key={`${m.role}-${i}-${m.content.length}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] px-4 py-3 rounded-2xl whitespace-pre-wrap text-[15px] leading-relaxed ${m.role === "user" ? "bg-teal-500 text-white rounded-br-md" : "bg-slate-100 text-slate-800 rounded-bl-md"}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start"><div className="bg-slate-100 px-4 py-3 rounded-2xl text-slate-500 text-sm">Aurora está pensando…</div></div>
            )}
          </div>
          {messages.length <= 1 && (
            <div className="px-6 pb-3 flex flex-wrap gap-2" data-testid="aurora-suggestions">
              {suggestions.map((s) => (
                <button key={s} onClick={()=>setInput(s)} className="px-3 py-1.5 rounded-full text-xs border border-slate-300 hover:border-teal-400 text-slate-700 bg-white">{s}</button>
              ))}
            </div>
          )}
          <div className="p-4 border-t border-slate-200 flex gap-2 items-end">
            <Textarea rows={1} value={input} onChange={(e)=>setInput(e.target.value)} onKeyDown={(e)=>{ if(e.key==="Enter" && !e.shiftKey){ e.preventDefault(); send(); }}}
              placeholder="Escreva o que estiver sentindo…" className="resize-none min-h-[48px] max-h-32" data-testid="aurora-input" />
            <Button onClick={send} disabled={busy} className="bg-teal-500 hover:bg-teal-600 h-12 w-12 p-0 rounded-xl" data-testid="aurora-send"><Send className="w-4 h-4" /></Button>
          </div>
        </CardContent>
      </Card>
      <p className="text-xs text-slate-500 mt-4 text-center">Se você estiver em crise, ligue <b>188 (CVV)</b> ou <b>192 (SAMU)</b>. Suporte 24h, sigiloso e gratuito.</p>
    </div>
  );
}
